import { useEffect, useState } from "react";
import { getPlayerInfo } from "../tools/PlayerTools";
import { callOption, computeCallPremium, computePutPremium, executeOption, getAvailableShares, getStockHistory, getStockPrices, getTransactionHistory, purchase, putOption, sell } from "../tools/StockTools";
import { useLocation, useNavigate } from "react-router-dom";
import React from 'react';
import { convertToCommaString } from "../helpers/Converter"
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import Spinner from "./Spinner";
import { applyShareSuspend } from "../tools/RoundTools";

export const CompanyInfo = () => {
    const [playerInfo, setPlayerInfo] = useState({});
    const [loading, setLoading] = useState(true);
    const location = useLocation();
    const [stockHistory, setStockHistory] = useState([]);
    const [stockPrice, setStockPrice] = useState({ current: 0, previous: 0 });
    const companyId = location.pathname.substring(location.pathname.lastIndexOf("/") + 1)
    const [useDebenture, setUseDebenture] = useState(false);
    const [useRightIssue, setUseRightIssue] = useState(false);
    const [units, setUnits] = useState(0);
    const [validity, setValidity] = useState(0);
    const [premium, setPremium] = useState(0);
    const [strikePrice, setStrikePrice] = useState(0);
    const [buyingPrice, setBuyingPrice] = useState(0);
    const [sellingPrice, setSellingPrice] = useState(0);
    const [hasRightIssue, setHasRightIssue] = useState(false);
    const [mode, setMode] = useState(null);
    const [availableShares, setAvailableShares] = useState(0);
    const [buyUnitText, setBuyUnitText] = useState("");
    const [sellUnitText, setSellUnitText] = useState("");
    const [callUnitText, setCallUnitText] = useState("");
    const [putUnitText, setPutUnitText] = useState("");
    const [width, setWidth] = useState(window.innerWidth);
    const [performingTransaction, setPerformingTransaction] = useState(false);
    const [transactionHistory, setTransactionHistory] = useState([]);
    const [playerHoldings, setPlayerHoldings] = useState([]);

    const navigate = useNavigate();

    const isNumeric = (str) => {
        return !isNaN(str) && str.trim() !== '';
    };

    useEffect(() => {
        const handleResize = () => setWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        if (!localStorage.getItem("token")) {
            window.location.href = "/";
            return;
        }
        document.title = `Company Info - ${companyId.toUpperCase()}`;
        getPlayerInfo().then(playerInfo => {
            if (playerInfo.error) {
                window.location.href = "/";
                return;
            }
            setPlayerInfo(playerInfo);
            getStockPrices().then(stockPrices => {
                if (stockPrices.error || !stockPrices[companyId]) {
                    window.location.href = "/";
                    return;
                }
                setStockPrice(stockPrices[companyId]);
                let hasDebenture = false;
                playerInfo.cards.forEach(card => {
                    if (card.debenture && card.active) {
                        hasDebenture = true;
                    }
                })
                let hasRightIssue = false;
                playerInfo.cards.forEach(card => {
                    if (card.rightIssue && card.active) {
                        hasRightIssue = true;
                    }
                })
                setHasRightIssue(hasRightIssue);
                if (stockPrices[companyId].current === 0) {
                    setUseDebenture(hasDebenture)
                    setUseRightIssue(hasRightIssue);
                }
                getStockHistory(companyId).then(data => {
                    if (data.error) {
                        window.location.href = "/";
                        return;
                    }
                    const history = [];
                    data.history.forEach((item, index) => {
                        history.push({
                            name: `${index + 1}`,
                            value: item
                        })
                    })
                    if (stockPrices[companyId].current === 0) {
                        if (hasDebenture) {
                            setSellingPrice(data.history[0]);
                        } if (hasRightIssue) {
                            setBuyingPrice(data.history[0]);
                        }
                    } else {
                        setBuyingPrice(stockPrices[companyId].current);
                        setSellingPrice(stockPrices[companyId].current);
                    }
                    setStockHistory(history);
                    getAvailableShares(companyId).then(availableShareResponse => {
                        if (availableShareResponse.error) {
                            window.location.href = "/";
                            return;
                        }
                        setAvailableShares(availableShareResponse.availableShares);
                        setPlayerHoldings(availableShareResponse.playerHoldings);
                        setLoading(false);
                    })
                });
            }
            )
        })
        getTransactionHistory(companyId).then(data => {
            if (!data.error) {
                setTransactionHistory(data.transactionHistory)
            }
        })
        return () => window.removeEventListener("resize", handleResize);
        //eslint-disable-next-line
    }, [])
    if (loading) {
        return (
            <div style={{ height: "100vh", textAlign: "center", alignItems: "center", justifyContent: "center", display: "flex", flexDirection: "column" }}>
                <h1>{`${companyId.toUpperCase()}`}</h1>
                <Spinner />
            </div>
        )
    }
    let color = "black";
    if (stockPrice.current > stockPrice.previous) {
        color = "green";
    } else if (stockPrice.current < stockPrice.previous) {
        color = "red";
    }

    let percentage = 0;
    let difference = 0;
    difference = stockPrice.current - stockPrice.previous;
    if (stockPrice.previous !== 0) {
        percentage = ((stockPrice.current - stockPrice.previous) / stockPrice.previous) * 100;
    }

    const InvestmentDetails = () => {
        let color = "black";
        let profit = 0;
        let totalProfit = 0;
        if (playerInfo.totalAmountInvested && playerInfo.stocks && (playerInfo.stocks[companyId] || 0) > 0) {
            const invested = Math.floor(playerInfo.totalAmountInvested[companyId] || 0);
            const current = 1000 * playerInfo.stocks[companyId] * stockPrice.current;
            if (invested > current) color = "#D32F2F";
            else if (current > invested) color = "#388E3C";
            profit = current - invested;
            totalProfit += 1000 * playerInfo.stocks[companyId] * stockPrice.current
        }
        transactionHistory.forEach(transaction => {
            if (transaction.mode === "buy") totalProfit -= transaction.totalPrice;
            else totalProfit += transaction.totalPrice;
        })
        return (
            <div style={{ width: "100%", marginTop: "25px", marginBottom: "25px" }}>
                {width >= 1080 && <div style={{ display: "flex" }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "25%" }}>
                        <div style={{ marginBottom: "5px" }}>Shares</div>
                        <div style={{ fontSize: "1.2rem", fontWeight: "bold", marginBottom: "10px" }}>{convertToCommaString(1000 * ((playerInfo.stocks && playerInfo.stocks[companyId]) || 0))}</div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "25%" }}>
                        <div style={{ marginBottom: "5px" }}>Invested</div>
                        <div style={{ fontSize: "1.2rem", fontWeight: "bold", marginBottom: "10px" }}>{"₹" + convertToCommaString((playerInfo.totalAmountInvested && Math.floor(playerInfo.totalAmountInvested[companyId] || 0)) || 0)}</div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "25%" }}>
                        <div style={{ marginBottom: "5px" }}>Average</div>
                        <div style={{ fontSize: "1.2rem", fontWeight: "bold", marginBottom: "10px" }}>{`₹${playerInfo.stocks && playerInfo.stocks[companyId] && playerInfo.stocks[companyId] > 0 ? (playerInfo.totalAmountInvested[companyId] / (1000 * playerInfo.stocks[companyId])).toFixed(2) : "0"}`}</div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "25%" }}>
                        <div style={{ marginBottom: "5px" }}>Current</div>
                        <div style={{ fontSize: "1.2rem", fontWeight: "bold", marginBottom: "10px", color }}>{"₹" + convertToCommaString(1000 * ((playerInfo.stocks && playerInfo.stocks[companyId]) || 0) * stockPrice.current)}</div>
                    </div>

                </div>}

                {width >= 1080 && <div style={{ display: "flex", marginTop: "25px", marginBottom: "25px" }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "25%" }}>
                        <div style={{ marginBottom: "5px" }}>P/L</div>
                        <div style={{ fontSize: "1.2rem", fontWeight: "bold", marginBottom: "10px", color }}>{(profit < 0 ? "-" : profit > 0 ? "+" : "") + "₹" + convertToCommaString(Math.floor(Math.abs(profit)))}</div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "25%" }}>
                        <div style={{ marginBottom: "5px" }}>%P/L</div>
                        <div style={{ fontSize: "1.2rem", fontWeight: "bold", marginBottom: "10px", color }}>{playerInfo.totalAmountInvested && (playerInfo.totalAmountInvested[companyId] || 0) > 0 ? ((profit >= 0 ? "+" : "-") + (Math.abs(100 * profit) / playerInfo.totalAmountInvested[companyId]).toFixed(2) + "%") : "0%"}</div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "25%" }}>
                        <div style={{ marginBottom: "5px" }}>Balance</div>
                        <div style={{ fontSize: "1.2rem", fontWeight: "bold", marginBottom: "10px" }}>{"₹" + convertToCommaString(Math.floor(playerInfo.balance))}</div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "25%" }}>
                        <div style={{ marginBottom: "5px" }}>Total P/L</div>
                        <div style={{ fontSize: "1.2rem", fontWeight: "bold", marginBottom: "10px", color: totalProfit < 0 ? "#CC0000" : totalProfit > 0 ? "#009900" : "black" }}>{`${totalProfit < 0 ? "-" : totalProfit > 0 ? "+" : ""}₹${convertToCommaString(totalProfit)}`}</div>
                    </div>

                </div>}


                {width < 1080 && <div style={{ display: "flex" }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "50%" }}>
                        <div style={{ marginBottom: "5px" }}>Shares</div>
                        <div style={{ fontSize: "1.4rem", fontWeight: "bold", marginBottom: "10px" }}>{convertToCommaString(1000 * ((playerInfo.stocks && playerInfo.stocks[companyId]) || 0))}</div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "50%" }}>
                        <div style={{ marginBottom: "5px" }}>Invested</div>
                        <div style={{ fontSize: "1.2rem", fontWeight: "bold", marginBottom: "10px" }}>{"₹" + convertToCommaString((playerInfo.totalAmountInvested && Math.floor(playerInfo.totalAmountInvested[companyId] || 0)) || 0)}</div>
                    </div>
                </div>}
                {width < 1080 && <div style={{ display: "flex", marginTop: "15px" }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "50%" }}>
                        <div style={{ marginBottom: "5px" }}>Average</div>
                        <div style={{ fontSize: "1.2rem", fontWeight: "bold", marginBottom: "10px" }}>{`₹${playerInfo.stocks && playerInfo.stocks[companyId] && playerInfo.stocks[companyId] > 0 ? (playerInfo.totalAmountInvested[companyId] / (1000 * playerInfo.stocks[companyId])).toFixed(2) : "0"}`}</div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "50%" }}>
                        <div style={{ marginBottom: "5px" }}>Current</div>
                        <div style={{ fontSize: "1.2rem", fontWeight: "bold", marginBottom: "10px", color }}>{"₹" + convertToCommaString(1000 * ((playerInfo.stocks && playerInfo.stocks[companyId]) || 0) * stockPrice.current)}</div>
                    </div>
                </div>}
                {width < 1080 && <div style={{ display: "flex", marginTop: "15px" }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "50%" }}>
                        <div style={{ marginBottom: "5px" }}>P/l</div>
                        <div style={{ fontSize: "1.2rem", fontWeight: "bold", marginBottom: "10px" }}>{(profit < 0 ? "-" : profit > 0 ? "+" : "") + "₹" + convertToCommaString(Math.floor(Math.abs(profit)))}</div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "50%" }}>
                        <div style={{ marginBottom: "5px" }}>%P/L</div>
                        <div style={{ fontSize: "1.2rem", fontWeight: "bold", marginBottom: "10px", color }}>{playerInfo.totalAmountInvested && (playerInfo.totalAmountInvested[companyId] || 0) > 0 ? ((profit >= 0 ? "+" : "-") + (Math.abs(100 * profit) / playerInfo.totalAmountInvested[companyId]).toFixed(2) + "%") : "0%"}</div>
                    </div>
                </div>}
                {width < 1080 && <div style={{ display: "flex", marginTop: "15px", position: "fixed", zIndex: 1000, bottom: 0, left: 0, right: 0, backgroundColor: "white", paddingBlock: "15px" }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "47%", backgroundColor: "black", marginInline: "1.5%", paddingBlock: "6px", borderRadius: "25px" }}>
                        <div style={{ marginTop: "6px", marginBottom: "10px", color: "white", fontSize: "0.8rem", fontWeight: "bold" }}>Balance</div>
                        <div style={{ fontSize: "1.2rem", fontWeight: "bold", marginBottom: "6px", color: "white" }}>{"₹" + convertToCommaString(Math.floor(playerInfo.balance))}</div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "47%", backgroundColor: totalProfit < 0 ? "#CC0000" : totalProfit > 0 ? "#009900" : "black", marginInline: "1.5%", paddingBlock: "6px", borderRadius: "25px" }}>
                        <div style={{ marginTop: "6px", marginBottom: "10px", color: "white", fontSize: "0.8rem", fontWeight: "bold" }}>Total P/L</div>
                        <div style={{ fontSize: "1.2rem", fontWeight: "bold", marginBottom: "6px", color: "white" }}>{`${totalProfit < 0 ? "-" : totalProfit > 0 ? "+" : ""}₹${convertToCommaString(totalProfit)}`}</div>
                    </div>
                </div>}
            </div>
        )
    }

    const computePremium = async () => {
        if (mode === "call") {
            computeCallPremium(companyId, units, validity, strikePrice).then(data => {
                if (data.error) {
                    alert(data.error)
                }
                setPremium(data.premium || 0);
            }).catch(error => {

            });
        } else if (mode === "put") {
            computePutPremium(companyId, units, validity, strikePrice).then(data => {
                if (data.error) {
                    alert(data.error)
                }
                setPremium(data.premium || 0);
            }).catch(error => {

            });
        }
    }

    const performTransaction = () => {
        setPerformingTransaction(true);
        if (mode === "buy") {
            purchase(companyId, units, useRightIssue).then(data => {
                if (data.error) {
                    alert(data.error)
                    setPerformingTransaction(false);
                } else {
                    navigate(`/game/${playerInfo.gameId}`)
                }
            }).catch(e => { })
        } else if (mode === "sell") {
            sell(companyId, units, useDebenture).then(data => {
                if (data.error) {
                    alert(data.error)
                    setPerformingTransaction(false);
                } else {
                    navigate(`/game/${playerInfo.gameId}`)
                }
            }).catch(e => { })
        } else if (mode === "call") {
            callOption(companyId, units, validity, strikePrice).then(data => {
                if (data.error) {
                    alert(data.error);
                    setPerformingTransaction(false);
                } else {
                    navigate(`/game/${playerInfo.gameId}`)
                }
            })
        } else if (mode === "put") {
            putOption(companyId, units, validity, strikePrice).then(data => {
                if (data.error) {
                    alert(data.error);
                    setPerformingTransaction(false);
                } else {
                    navigate(`/game/${playerInfo.gameId}`)
                }
            })
        }
    }

    if (mode === "call" || mode === "put") {
        return <div style={{ width: "100vw", height: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: width >= 1080 ? "center" : "flex-start", paddingBlock: width >= 1080 ? "0px" : "20vh" }}>
            <input type="number"
                autoFocus={true}
                placeholder={`Enter the shares you wish to ${mode === "call" ? "buy" : "sell"}`}
                value={mode === "call" ? callUnitText : putUnitText}
                onChange={(e) => {
                    if (mode === "call") {
                        if (e.target.value.trim() === "") {
                            setBuyUnitText("");
                            setSellUnitText("");
                            setCallUnitText("");
                            setPutUnitText("");
                            setUnits(0);
                        }
                        if (isNumeric(e.target.value)) {
                            if (Number(e.target.value) < 1000) {
                                setBuyUnitText("");
                                setSellUnitText("");
                                setCallUnitText(e.target.value);
                                setPutUnitText("");
                                setUnits(e.target.value)
                            }
                        }
                    } else {
                        if (e.target.value.trim() === "") {
                            setBuyUnitText("");
                            setSellUnitText("");
                            setCallUnitText("");
                            setPutUnitText("");
                            setUnits(0);
                        }
                        if (isNumeric(e.target.value)) {
                            setBuyUnitText("");
                            setSellUnitText("");
                            setCallUnitText("");
                            setPutUnitText(e.target.value);
                            setUnits(e.target.value)
                        }
                    }
                    setPremium(0);
                }}
                style={{
                    width: `${width >= 1080 ? "500px" : "90%"}`,
                    border: "none",
                    borderBottom: "2px solid #333",
                    outline: "none",
                    background: "transparent",
                    padding: "5px",
                    fontSize: "16px",
                }}
            />
            <p style={{
                width: `${width >= 1080 ? "500px" : "90%"}`,
                fontSize: "12px",
                marginTop: "0px"
            }}>{`${convertToCommaString(units * 1000)} shares`}</p>
            <input type="number"
                placeholder={`Enter the strike price`}
                value={strikePrice || ""}
                onChange={(e) => {
                    if (isNumeric(e.target.value)) {
                        setStrikePrice(Math.floor(e.target.value))
                    } else {
                        setStrikePrice(0)
                    }
                    setPremium(0);
                }}
                style={{
                    width: `${width >= 1080 ? "500px" : "90%"}`,
                    marginTop: "20px",
                    border: "none",
                    borderBottom: "2px solid #333",
                    outline: "none",
                    background: "transparent",
                    padding: "5px",
                    fontSize: "16px",
                }}
            />
            <p style={{
                width: `${width >= 1080 ? "500px" : "90%"}`,
                fontSize: "12px",
                marginTop: "0px"
            }}>{`₹${strikePrice} per share`}</p>

            <input type="number"
                placeholder={`Enter the validity in rounds`}
                value={validity || ""}
                onChange={(e) => {
                    if (isNumeric(e.target.value)) {
                        setValidity(Math.floor(e.target.value))
                    } else {
                        setValidity(0)
                    }
                    setPremium(0);
                }}
                style={{
                    width: `${width >= 1080 ? "500px" : "90%"}`,
                    marginTop: "20px",
                    border: "none",
                    borderBottom: "2px solid #333",
                    outline: "none",
                    background: "transparent",
                    padding: "5px",
                    fontSize: "16px",
                }}
            />
            <p style={{
                width: `${width >= 1080 ? "500px" : "90%"}`,
                fontSize: "12px",
                marginTop: "0px"
            }}>{`Valid for ${validity} rounds, applicable from next round`}</p>

            {<p style={{ marginInline: "5px" }}>{performingTransaction ? 'Placing your order' : `${(units > 0 && validity > 0 && strikePrice > 0 && premium > 0) ? `You wish to ${mode === "call" ? "buy" : "sell"} ${convertToCommaString(units * 1000)} shares at ₹${strikePrice} per share within the next ${validity} rounds. It will cost ₹${convertToCommaString(premium)}` : ""}`}</p>}
            {!performingTransaction && <div style={{ display: "flex", marginTop: "20px" }}>
                <button style={{ width: "100px", borderRadius: "20px", border: "none", marginInline: "10px", padding: "10px", background: "#CC0000", color: "white", fontSize: "1rem", fontWeight: "bold", cursor: "pointer" }} onClick={() => setMode(null)}>Cancel</button>
                {premium > 0 ? <button style={{ width: "100px", borderRadius: "20px", border: "none", marginInline: "10px", padding: "10px", background: "#009900", color: "white", fontSize: "1rem", fontWeight: "bold", cursor: "pointer" }} onClick={performTransaction}>Submit</button> : <button style={{ width: "100px", borderRadius: "20px", border: "none", marginInline: "10px", padding: "10px", background: "#000088", color: "white", fontSize: "1rem", fontWeight: "bold", cursor: "pointer" }} onClick={computePremium}>Charges</button>}
            </div>}
        </div>
    }

    if (mode != null && ((mode === "buy" && buyingPrice > 0) || (mode === "sell" && sellingPrice > 0))) {
        return <div style={{ width: "100vw", height: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: width >= 1080 ? "center" : "flex-start", paddingBlock: width >= 1080 ? "0px" : "20vh" }}>
            <input type="number"
                autoFocus={true}
                placeholder={"Enter shares to " + mode}
                value={mode === "buy" ? buyUnitText : sellUnitText}
                onChange={(e) => {
                    if (mode === "buy") {
                        if (e.target.value.trim() === "") {
                            setBuyUnitText("");
                            setSellUnitText("")
                            setCallUnitText("");
                            setPutUnitText("");
                            setUnits(0);
                        }
                        if (isNumeric(e.target.value)) {
                            setSellUnitText("")
                            setCallUnitText("");
                            setPutUnitText("");
                            setBuyUnitText(e.target.value);
                            if (Number(e.target.value) < 1000) {
                                setUnits(e.target.value)
                            } else {
                                setUnits(Math.floor(Number(e.target.value) / (1000 * buyingPrice)))
                            }
                        }
                    } else {
                        if (e.target.value.trim() === "") {
                            setSellUnitText("");
                            setBuyUnitText("");
                            setCallUnitText("");
                            setPutUnitText("");
                            setUnits(0);
                        }
                        if (isNumeric(e.target.value)) {
                            setMode("sell");
                            setCallUnitText("");
                            setPutUnitText("");
                            setSellUnitText(e.target.value);
                            setBuyUnitText("");
                            if (Number(e.target.value) < 1000) {
                                setUnits(e.target.value)
                            } else {
                                setUnits(Math.ceil(Number(e.target.value) / (1000 * sellingPrice)))
                            }
                        }
                    }
                }}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        performTransaction();
                    }
                }}
                style={{
                    width: `${width >= 1080 ? "500px" : "90%"}`,
                    border: "none",
                    borderBottom: "2px solid #333",
                    outline: "none",
                    background: "transparent",
                    padding: "5px",
                    fontSize: "16px",
                }}
            />
            {mode === "buy" && hasRightIssue && (playerInfo.stocks[companyId] || 0 > 2) && <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginTop: "20px" }}>
                <input type="checkbox" checked={useRightIssue} onChange={(e) => {
                    setUseRightIssue(e.target.checked);
                    if (e.target.checked) {
                        setBuyingPrice(10);
                    } else {
                        setBuyingPrice(stockPrice.current);
                    }
                }} />
                <span>Use Rights Issue Card</span>
            </div>}
            {units > 0 && <p>{`${mode.substring(0, 1).toUpperCase() + mode.substring(1)}${performingTransaction ? "ing" : ""} ${convertToCommaString(1000 * units)} shares for ₹${convertToCommaString(1000 * units * (mode === "buy" ? buyingPrice : sellingPrice))}`}</p>}
            {!performingTransaction && <div style={{ display: "flex", marginTop: "20px" }}>
                <button style={{ width: "100px", borderRadius: "20px", border: "none", marginInline: "10px", padding: "10px", background: "#CC0000", color: "white", fontSize: "1rem", fontWeight: "bold", cursor: "pointer" }} onClick={() => setMode(null)}>Cancel</button>
                <button style={{ width: "100px", borderRadius: "20px", border: "none", marginInline: "10px", padding: "10px", background: "#009900", color: "white", fontSize: "1rem", fontWeight: "bold", cursor: "pointer" }} onClick={performTransaction}>Submit</button>
            </div>}
        </div>
    }

    return (
        <div style={{ height: "100vh", padding: "5px" }}>
            {width >= 1080 && <h2 style={{ marginLeft: "10px" }}>{companyId.toUpperCase() + " (" + convertToCommaString(availableShares) + " shares available)"}</h2>}
            {width < 1080 && <h3 style={{ marginLeft: "10px" }}>{companyId.toUpperCase() + " (" + convertToCommaString(availableShares) + " shares available)"}</h3>}
            <div style={{ display: "flex", flexDirection: `${width >= 1080 ? "row" : "column"}` }}>
                <div style={{ height: "200px", width: `${width >= 1080 ? "70%" : "100%"}` }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={stockHistory} dataKey={"value"} margin={{ top: 5, left: 5, right: 10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis width={0} dataKey={"value"} domain={["dataMin - 10", "dataMax + 10"]} />
                            <Tooltip />
                            <Legend />
                            <Line type="monotone" dataKey="value" stroke="#8884d8" activeDot={{ r: 8 }} />
                        </LineChart>
                    </ResponsiveContainer>
                    {width >= 1080 && <InvestmentDetails />}
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", marginTop: "20px", width: `${width >= 1080 ? "30%" : "100%"}` }}>
                    <h2 style={{ color: color }}>{"₹" + stockPrice.current.toFixed(2)}</h2>
                    <p style={{ color: color, marginTop: "-12px", marginBottom: "20px", fontSize: "0.8rem" }}>{`${difference >= 0 ? "+" : ""}${difference.toFixed(2)} (${Math.abs(percentage).toFixed(2)}%)`}</p>
                    {playerInfo.chance && <div style={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "center" }}>
                        <button style={{ width: "45%", borderRadius: "20px", border: "none", marginInline: "2.5%", padding: "10px", background: "#009900", color: "white", fontSize: "1rem", fontWeight: "bold", cursor: "pointer" }} onClick={() => setMode("buy")}>Buy</button>
                        <button style={{ width: "45%", borderRadius: "20px", border: "none", marginInline: "2.5%", padding: "10px", background: "#CC0000", color: "white", fontSize: "1rem", fontWeight: "bold", cursor: "pointer" }} onClick={() => setMode("sell")}>Sell</button>
                    </div>}
                    {playerInfo.chance && <div style={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "center", marginTop: "20px" }}>
                        <button style={{ width: "45%", borderRadius: "20px", border: "none", marginInline: "2.5%", padding: "10px", background: "#009900", color: "white", fontSize: "1rem", fontWeight: "bold", cursor: "pointer" }} onClick={() => setMode("call")}>Call</button>
                        <button style={{ width: "45%", borderRadius: "20px", border: "none", marginInline: "2.5%", padding: "10px", background: "#CC0000", color: "white", fontSize: "1rem", fontWeight: "bold", cursor: "pointer" }} onClick={() => setMode("put")}>Put</button>
                    </div>}
                    {
                        playerInfo.chance && playerInfo.options.map(option => {
                            if (option.company === companyId) {
                                return (<div style={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "center", marginTop: "20px" }}>
                                    <button style={{ width: "95%", borderRadius: "50px", border: "none", marginInline: "2.5%", padding: "10px", background: "#6750A4", color: "white", fontSize: "1rem", fontWeight: "bold", cursor: "pointer" }} onClick={() => {
                                        executeOption(option.id)
                                            .then(data => {
                                                if (data.error) {
                                                    alert(data.error);
                                                } else {
                                                    navigate(`/game/${playerInfo.gameId}`);
                                                }
                                            })
                                    }}><div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
                                            <text>{`${option.mode === "call" ? "Buy" : "Sell"} ${convertToCommaString(option.units * 1000)} shares at ₹${option.strikePrice} per share`}</text>
                                            <text>{`Valid upto round ${option.validUpto + 1}`}</text>
                                        </div>
                                    </button>
                                </div>)
                            }
                            return <div></div>
                        })
                    }
                    {playerInfo.chance && (hasRightIssue && playerInfo.stocks[companyId] && playerInfo.stocks[companyId] >= 2) && <div style={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "center", padding: "20px" }}>
                        <p style={{ marginTop: "-5px", marginLeft: "10px", marginRight: "10px", textAlign: "center" }}>{`You can buy up to ${Math.floor(playerInfo.stocks[companyId] / 2) * 1000} shares at ₹10 per share using rights issue`}</p>
                    </div>}
                    {playerInfo.chance && (useDebenture && playerInfo.stocks[companyId] && playerInfo.stocks[companyId] > 0 && stockPrice.current === 0) && <div style={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "center", padding: "10px" }}>
                        <p style={{ marginTop: "-5px", marginLeft: "10px", marginRight: "10px", textAlign: "center" }}>{`You can sell your shares at ₹${sellingPrice} per share`}</p>
                    </div>}
                    {playerInfo.applyShareSuspend && stockPrice.previous !== stockPrice.current && <div style={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "center" }}>
                        <button style={{ width: "95%", borderRadius: "20px", border: "none", marginInline: "2.5%", padding: "10px", background: "#6750A4", color: "white", fontSize: "1rem", fontWeight: "bold", cursor: "pointer" }} onClick={() => {
                            applyShareSuspend(companyId).then(data => {
                                if (data.error) {
                                    alert(data.error);
                                } else {
                                    navigate(`/game/${playerInfo.gameId}`);
                                }
                            })
                        }}>{`Change ${companyId.toUpperCase()} price from ${stockPrice.current} to ${stockPrice.previous}`}</button>
                    </div>}
                    {width < 1080 && <InvestmentDetails />}
                    {playerHoldings.length > 0 && <div style={{ width: "100%" }}>
                        <h3>Player wise holdings</h3>
                        {playerHoldings.map((holdingInfo) => {
                            return <div key={"player_holdings_" + companyId + "_" + holdingInfo.id} style={{ width: "auto", display: "flex", padding: "15px", alignItems: "center" }}>
                                <img src={holdingInfo.photoUrl} alt="player pic" width="100px" style={{ borderRadius: "50px", marginRight: "15px" }} />
                                <div style={{ width: "auto" }}>
                                    <p style={{
                                        fontSize: "1rem",
                                        fontWeight: "bold"
                                    }}>{holdingInfo.name}</p>
                                    <p style={{
                                        fontSize: "1rem",
                                        fontWeight: "bold",
                                        marginTop: "-12px"
                                    }}>{convertToCommaString(holdingInfo.holding) + " Shares"}</p>
                                    {holdingInfo.holding >= 50000 && <p style={{
                                        fontSize: "0.7rem",
                                        marginTop: "-12px"
                                    }}>{holdingInfo.holding >= 100000 ? "Chairman" : "Vice Chairman"}</p>}
                                </div>
                            </div>
                        })}
                    </div>}
                    {transactionHistory.length > 0 && <div style={{ width: "100%" }}>
                        <h3>Transaction History</h3>
                        {transactionHistory.map((transaction) => {
                            const time = new Date(transaction.timestamp);
                            return <div key={"transaction_" + transaction.timestamp} style={{ width: "auto", display: "flex", padding: "15px", alignItems: "center" }}>
                                <img src={`${process.env.REACT_APP_SERVER_URL}/image/${companyId}.png`} alt="company pic" width="100px" style={{ marginRight: "15px" }} />
                                <div style={{ width: "auto" }}>
                                    <p style={{
                                        fontSize: "1rem",
                                        fontWeight: "bold",
                                        color: transaction.mode === "buy" ? "#009900" : "#CC0000"
                                    }}>{(transaction.mode === "buy" ? "+" : "-") + convertToCommaString(1000 * transaction.actualUnits) + " Shares"}</p>
                                    <p style={{
                                        fontSize: "1rem",
                                        fontWeight: "bold",
                                        color: transaction.mode === "buy" ? "#009900" : "#CC0000",
                                        marginTop: "-12px"
                                    }}>{"₹" + convertToCommaString(transaction.totalPrice)}</p>
                                    <p style={{
                                        fontSize: "0.7rem",
                                        marginTop: "-12px"
                                    }}>{time.getDate().toString().padStart(2, "0") + "/" + (1 + time.getMonth()).toString().padStart(2, "0") + "/" + time.getFullYear() + " at " + time.getHours().toString().padStart(2, "0") + ":" + time.getMinutes().toString().padStart(2, "0")}</p>
                                    <p style={{
                                        fontSize: "0.7rem",
                                        marginTop: "-12px"
                                    }}>{"Round: " + (1 + transaction.round) + ", Chance: " + (1 + transaction.chance)}</p>
                                </div>
                            </div>
                        })}
                    </div>}
                </div>
            </div>
            {width < 1080 && <div style={{ height: "120px" }}></div>}
        </div>
    );
}